import React, { createContext, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { usePrefs } from 'wsm-common-data';
import { hysteriFetch, wrappers } from 'hysterics';
import { setCurrAvgAndTotalRating } from '../actions/reviewsInfo';
import { setReviews } from '../actions/reviews';
import { setReviewsFilters } from '../actions/reviewsFilters';
import { setPage } from '../actions/page';
import { buildUrl, mergeObjects } from '../utils';
import { stateToStringArray, stateToSearchParams } from '../helpers';

export const FetchReviewsContext = createContext();

const FetchReviewsProvider = ({ children, cache }) => {
	// Shared state
	const [requestState, setRequestState] = useState({
		idle: true,
		isLoading: false,
		error: null
	});
	// Pull dealerId from redux store
	const { dealerId, reviewsFilters } = useSelector((state) => ({
		dealerId: state.dealerId,
		reviewsFilters: state.reviewsFilters
	}));
	// Defines the max number of reviews per page
	const { reviewsPerPage } = usePrefs();
	const dispatch = useDispatch();

	// Unit tests are easier to write if we can mock the reviews service
	// doing this, means that a new service is created for every test
	// we use useMemo to prevent the service from being recreated on every render
	const reviewsService = useMemo(() => {
		return hysteriFetch('reviews', {
			[wrappers.timeout.contextKey]: {
				timeout: 2000
			},
			[wrappers.retry.contextKey]: {
				// We already have one retry in the fetchReviews function
				retries: 0
			}
		});
	}, []);

	const fetchReviews = async (newPage, queries, updateHistory = true) => {
		setRequestState({ idle: false, isLoading: true, error: null });
		const mergedQueries = mergeObjects(reviewsFilters, queries);
		const url = buildUrl(
			'./api/widget/ws-vw-reviews/reviews',
			{
				ratings: stateToStringArray(mergedQueries.ratings).sort(),
				models: stateToStringArray(mergedQueries.models).sort(),
				types: mergedQueries.types.selected,
				page: newPage,
				vwDealerId: dealerId,
				maxSize: reviewsPerPage
			},
			window.location.origin
		);
		const urlString = url.toString();
		let response;
		try {
			if (cache?.has(url.search)) {
				response = cache?.get(url.search);
				// synthetic delay
				await new Promise((resolve) => setTimeout(resolve, 50));
			} else {
				response = await reviewsService(urlString);
			}
			if (!response) {
				throw new Error('No response from reviews service');
			}
			cache?.set(url.search, response);
		} catch (error) {
			// If there is an error, we want to reset the reviewsFilters
			dispatch(setReviewsFilters(reviewsFilters));
			return setRequestState({
				idle: false,
				isLoading: false,
				error
			});
		}
		if (updateHistory) {
			const searchParams = stateToSearchParams(mergedQueries, {
				page: newPage
			});
			window.history.pushState(
				{
					page: newPage,
					reviewsFilters: mergedQueries
				},
				'',
				`?${searchParams.toString()}`
			);
		}

		dispatch(
			setCurrAvgAndTotalRating(
				response.currentAvgRating,
				response.currentTotalRatings
			)
		);
		dispatch(setReviews(response.reviews));
		dispatch(setPage(newPage));
		return setRequestState({
			idle: false,
			isLoading: false,
			error: null
		});
	};

	const value = {
		isLoading: requestState.isLoading,
		idle: requestState.idle,
		error: requestState.error,
		fetchReviews
	};
	return (
		<FetchReviewsContext.Provider value={value}>
			{children}
		</FetchReviewsContext.Provider>
	);
};

FetchReviewsProvider.propTypes = {
	children: PropTypes.node.isRequired,
	cache: PropTypes.shape({
		has: PropTypes.func.isRequired,
		get: PropTypes.func.isRequired,
		set: PropTypes.func.isRequired
	}).isRequired
};

export default FetchReviewsProvider;
